import { isValidElement, type ReactNode } from 'react';

// Plain text of a ReactNode; non-text children (icons etc.) are ignored.
// Note: JSX passed from a Server Component arrives as lazy nodes during SSR and yields no text,
// so server-rendered callers should pass LeadButton an explicit title built with getLeadButtonTitle.
const getNodeText = (node: ReactNode): string => {
    if (node == null || typeof node === 'boolean') return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(getNodeText).join(' ');
    if (isValidElement(node)) return getNodeText((node.props as { children?: ReactNode }).children);
    return '';
};

// Default title mirrors the CTA label, expanded to the full car name when the label only names the model,
// e.g. carName "BMW 3 Series" + label "3 Series Mileage" -> "BMW 3 Series Mileage".
// "Notify Me" -> "Notify Me About {carName}"; other generic labels (Check Offers, ...) keep "{carName} Offers".
export const getLeadButtonTitle = (carName: string, btnText: ReactNode): string | undefined => {
    if (!carName) return undefined;
    const name = carName.replace(/\s+/g, ' ').trim();
    const label = getNodeText(btnText).replace(/\s+/g, ' ').trim();
    const lowerLabel = label.toLowerCase();

    if (label && lowerLabel.includes(name.toLowerCase())) return label;
    if (lowerLabel.startsWith('notify me')) return `Notify Me About ${name}`;

    const words = name.split(' ');
    for (let i = 1; i < words.length; i++) {
        const suffix = words.slice(i).join(' ').toLowerCase();
        if (lowerLabel === suffix || lowerLabel.startsWith(`${suffix} `)) {
            return `${name}${label.slice(suffix.length)}`;
        }
    }

    return `${name} Offers`;
};
